import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Download, FileWarning, Loader2, Upload } from 'lucide-react';
import { leaveAPI } from '../lib/api';

export default function LeaveImport() {
  const navigate = useNavigate();
  const [kind, setKind] = useState('balances');
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [partial, setPartial] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await leaveAPI.importTemplate(kind);
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leave_${kind}_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Template download failed');
    }
  };

  const handleValidate = async () => {
    if (!file) return;
    setValidation(null);
    setCommitted(null);
    try {
      const r = await leaveAPI.importValidate(kind, file);
      setValidation(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Validation failed');
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const r = await leaveAPI.importCommit(kind, file, partial);
      setCommitted(r.data);
      if (r.data.committed) {
        toast.success(`Imported — inserted ${r.data.result?.inserted || 0}, updated ${r.data.result?.updated || 0}, skipped ${r.data.result?.skipped || 0}`);
      } else {
        toast.error('Commit blocked due to validation errors. Enable "Partial import" to skip invalid rows.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="leave-import-page">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/leave')} className="mb-1">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Leave
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Upload className="h-6 w-6 text-emerald-600" /> Leave Bulk Import
          </h1>
          <p className="text-sm text-slate-500 mt-1">Validate first — commit only after reviewing the dry-run report.</p>
        </div>

        <div className="rounded-lg border bg-white p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Import type</Label>
              <Select value={kind} onValueChange={(v) => { setKind(v); setFile(null); setValidation(null); setCommitted(null); }}>
                <SelectTrigger data-testid="import-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="balances">Leave balances</SelectItem>
                  <SelectItem value="requests">Leave requests</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Template</Label>
              <Button variant="outline" onClick={downloadTemplate} data-testid="import-template-btn">
                <Download className="h-4 w-4 mr-1.5" /> Download XLSX
              </Button>
            </div>
            <div>
              <Label>File</Label>
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setValidation(null); setCommitted(null); }}
                className="block w-full text-sm border rounded-md px-2 py-1.5"
                data-testid="import-file"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleValidate} disabled={!file} data-testid="import-validate-btn">
              Dry-run validate
            </Button>
            <label className="flex items-center gap-2 text-xs text-slate-600 ml-2">
              <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} data-testid="import-partial" />
              Partial import (skip invalid rows)
            </label>
            <Button
              onClick={handleCommit}
              disabled={!file || committing}
              data-testid="import-commit-btn"
            >
              {committing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1.5" />}
              Commit import
            </Button>
          </div>
        </div>

        {validation && (
          <div className="rounded-lg border bg-white p-4 space-y-2" data-testid="validation-result">
            <div className="flex items-center gap-2">
              {validation.ok ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <FileWarning className="h-4 w-4 text-amber-600" />}
              <h3 className="font-medium">Validation: {validation.ok ? 'ALL ROWS VALID' : 'ERRORS DETECTED'}</h3>
              <span className="text-sm text-slate-500 ml-2">{validation.valid_rows} valid · {validation.invalid_rows} invalid</span>
            </div>
            {validation.errors?.length > 0 && (
              <ul className="text-xs text-rose-700 list-disc pl-5">
                {validation.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            {(validation.rows || []).length > 0 && (
              <div className="rounded border overflow-auto max-h-72">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr><th className="px-2 py-1.5 text-left">Row</th><th className="px-2 py-1.5 text-left">Detail</th><th className="px-2 py-1.5 text-left">Errors</th></tr>
                  </thead>
                  <tbody>
                    {validation.rows.map((r, idx) => (
                      <tr key={idx} className={`border-t ${r.errors?.length ? 'bg-rose-50/30' : ''}`}>
                        <td className="px-2 py-1 font-mono">{r.row}</td>
                        <td className="px-2 py-1">
                          {kind === 'balances'
                            ? `${r.leave_type_name} · annual=${r.annual_days} used=${r.used_days} year=${r.year}`
                            : `${r.leave_type_name} · ${r.start_date} → ${r.end_date} · ${r.days}d`}
                        </td>
                        <td className="px-2 py-1 text-rose-700">{(r.errors || []).join('; ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {committed && (
          <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-4 text-sm" data-testid="commit-result">
            <h3 className="font-medium text-emerald-900">Commit result</h3>
            <div className="text-emerald-800 mt-1">
              Inserted: <b>{committed.result?.inserted || 0}</b> · Updated: <b>{committed.result?.updated || 0}</b> · Skipped: <b>{committed.result?.skipped || 0}</b>
            </div>
            {(committed.result?.errors || []).length > 0 && (
              <ul className="text-xs text-rose-700 list-disc pl-5 mt-1">
                {committed.result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
